import 'vite/modulepreload-polyfill';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { createApp, onMounted, onUnmounted, ref, watch } from 'vue';

import { useViewportSize } from '~/composables/use-viewport-size';
import { ANIMATION_CONFIGS } from '~/constants/animations';
import * as DataDogLogs from '~/lib/datadog/datadog';

import * as AnalyticsData from '../analytics/analytics-data';
import { BaseSection } from './base-section';

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const CAROUSEL_OPTIONS = {
  breakpoints: {
    0: {
      slidesPerView: 1.3,
    },
    768: {
      slidesPerView: 2,
    },
  },
  centeredSlides: true,
  loop: true,
  modules: [Navigation],
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
  roundLengths: true,
  updateOnWindowResize: true,
};

const SECTION_NAME = 'large-product-carousel';

class LargeProductCarousel extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const sectionInstance = this;

    createApp({
      name: 'LargeProductCarouselSection',
      setup() {
        const swiperInstance = ref(null);
        const activeCategory = ref(null);
        const activeProduct = ref({
          mensUrl: '',
          price: '',
          title: '',
          womensUrl: '',
        });
        const arrowHandlers = ref(null);
        const { isMobile } = useViewportSize();

        const updateActiveProduct = () => {
          if (!swiperInstance.value) return;

          const currentSlide = swiperInstance.value.slides[swiperInstance.value.activeIndex];
          if (!currentSlide) return;

          activeProduct.value = {
            description: currentSlide.getAttribute('data-product-description'),
            mensUrl: currentSlide.getAttribute('data-product-mens-url'),
            price: currentSlide.getAttribute('data-product-price'),
            title: currentSlide.getAttribute('data-product-title'),
            womensUrl: currentSlide.getAttribute('data-product-womens-url'),
          };
        };

        const setupArrowsListeners = () => {
          const mainCarousel = container.querySelector(`[data-${SECTION_NAME}-swiper]`);
          const prevButton = mainCarousel.querySelector('.swiper-button-prev');
          const nextButton = mainCarousel.querySelector('.swiper-button-next');

          const handleMouseMove = (e) => {
            const { height, left, top, width } = mainCarousel.getBoundingClientRect();
            const cursorX = e.clientX - left;
            const cursorY = e.clientY - top;
            const offsetTop = 0.2; // 20% from the top
            const offsetBottom = 0.75; // 75% from the bottom

            const isInTopOffset = cursorY < height * offsetTop;
            const isInBottomOffset = cursorY > height * offsetBottom;

            const midPointX = width / 2;

            if (isInTopOffset || isInBottomOffset) {
              prevButton.style.opacity = 0;
              nextButton.style.opacity = 0;
            } else {
              if (cursorX < midPointX) {
                prevButton.style.left = `${cursorX}px`;
                prevButton.style.top = `${cursorY}px`;
                prevButton.style.opacity = 1;
                nextButton.style.opacity = 0;
              } else {
                nextButton.style.left = `${cursorX}px`;
                nextButton.style.top = `${cursorY}px`;
                nextButton.style.opacity = 1;
                prevButton.style.opacity = 0;
              }
            }
          };

          const handleMouseLeave = () => {
            prevButton.style.opacity = 0;
            nextButton.style.opacity = 0;
          };

          container.addEventListener('mousemove', handleMouseMove);
          container.addEventListener('mouseleave', handleMouseLeave);

          return { handleMouseLeave, handleMouseMove };
        };

        const removeArrowsListeners = (handlers) => {
          if (handlers) {
            container.removeEventListener('mousemove', handlers.handleMouseMove);
            container.removeEventListener('mouseleave', handlers.handleMouseLeave);
          }
        };

        const handleCategorySwitch = async (categoryId) => {
          activeCategory.value = categoryId;

          // Find the carousel container for this category
          const categoryContainer = container.querySelector(
            `[data-${SECTION_NAME}-block-id="${categoryId}"]`,
          );
          if (!categoryContainer) return;

          // Get all slides from the new category
          const newSlides = categoryContainer.querySelectorAll('.swiper-slide');

          // Update the current splide instance with new slides
          if (swiperInstance.value) {
            swiperInstance.value.destroy();
          }

          // Move slides to main carousel
          const mainCarousel = container.querySelector(`[data-${SECTION_NAME}-swiper]`);
          const mainList = mainCarousel.querySelector('.swiper-wrapper');
          mainList.innerHTML = '';
          Array.from(newSlides).forEach((slide) => {
            mainList.appendChild(slide.cloneNode(true));
          });

          // Reinitialize Splide with new slides
          swiperInstance.value = new Swiper(mainCarousel, CAROUSEL_OPTIONS);
          swiperInstance.value.on('slideChange', updateActiveProduct);
          updateActiveProduct();
        };

        onMounted(async () => {
          const initialCategoryId = container.getAttribute('data-initial-category-id');

          try {
            if (initialCategoryId) {
              await handleCategorySwitch(initialCategoryId);
            }
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - Large Product Carousel - onMounted - Error initializing carousel',
              { error },
              error,
            );
          }

          if (!isMobile.value) {
            arrowHandlers.value = setupArrowsListeners();
          }

          // Hook analytics event to CTA links
          if (sectionInstance.isHomePageTemplate()) {
            AnalyticsData.hookHomepageModuleClicked({
              sectionName: SECTION_NAME,
            });
          }
        });

        onUnmounted(() => {
          removeArrowsListeners(arrowHandlers.value);
        });

        watch(isMobile, (newVal) => {
          if (newVal) {
            removeArrowsListeners(arrowHandlers.value);
            arrowHandlers.value = null;
          } else {
            arrowHandlers.value = setupArrowsListeners();
          }
        });

        return { activeCategory, activeProduct, handleCategorySwitch };
      },
    }).mount(container);
  }

  /**
   * Sets up a ScrollTrigger animation instead of using IntersectionObserver
   * This is used to initialize the animation with ScrollTrigger instead of the default behavior
   *
   * @param {Object} instanceData - The section instance data
   */
  setupInViewAnimation(instanceData) {
    if (instanceData.animation) {
      instanceData.animation.paused(false);

      ScrollTrigger.refresh();
    } else {
      const animation = this.setupAnimation(instanceData, () =>
        this.setupInViewAnimationTimeline(instanceData),
      );

      if (animation && animation.scrollTrigger) {
        instanceData.scrollTrigger = animation.scrollTrigger;
      }
    }
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    if (!container) return null;

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) return null;
    tl.fromTo(container, { opacity: 0 }, ANIMATION_CONFIGS.FADE_IN, 0);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new LargeProductCarousel();
});
